import React from "react";

import { useTable } from "react-table";
import { styled } from "styled-components";

import { IndeterminateCheckbox } from "shared/ui/table/IndeterminateCheckbox";

/**
 * 공용 데이터 표 (순수 표시 전용).
 *
 * 페이징은 모른다 — 받은 `data`(현재 페이지 행)만 그린다. 선택 상태도 소유하지 않고
 * `selection`(useTableSelection 결과)을 받아 체크박스만 그린다.
 *
 * @param columns   react-table 컬럼 정의 (페이지에서 정의)
 * @param data      현재 페이지 행 배열
 * @param selectable 체크박스 열 + 헤더 전체선택(현재 페이지) 표시 여부
 * @param selection  useTableSelection 결과 (selectable일 때 필요)
 * @param getRowId   행 → 고유 id (기본 row.id)
 * @param onRowClick 행 클릭 콜백 (선택 대신 상세 이동 등)
 * @param emptyMessage 데이터가 없을 때 문구
 */
export function DataTable({
  columns,
  data,
  selectable = false,
  selection,
  getRowId = (row) => row.id,
  onRowClick,
  emptyMessage = "데이터가 없습니다.",
}) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data });

  const pageAllSelected =
    selectable && selection ? selection.isPageAllSelected(data) : false;
  const pageSomeSelected =
    selectable && selection ? selection.isPageSomeSelected(data) : false;

  const totalColumnCount =
    (selectable ? 1 : 0) + (headerGroups[0]?.headers.length ?? 0);

  return (
    <TableWrap>
      <Table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...headerGroupProps } =
              headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} {...headerGroupProps}>
                {selectable && (
                  <Th $selectCell>
                    <IndeterminateCheckbox
                      checked={pageAllSelected}
                      indeterminate={pageSomeSelected && !pageAllSelected}
                      onChange={() => selection.togglePage(data)}
                    />
                  </Th>
                )}
                {headerGroup.headers.map((column) => {
                  const { key: colKey, ...colProps } = column.getHeaderProps();
                  return (
                    <Th key={colKey} {...colProps}>
                      {column.render("Header")}
                    </Th>
                  );
                })}
              </tr>
            );
          })}
        </thead>

        <tbody {...getTableBodyProps()}>
          {rows.length === 0 ? (
            <tr>
              <EmptyTd colSpan={totalColumnCount}>{emptyMessage}</EmptyTd>
            </tr>
          ) : (
            rows.map((row) => {
              prepareRow(row);
              const selected =
                selectable && selection ? selection.isSelected(row.original) : false;
              const { key, ...rowProps } = row.getRowProps();
              return (
                <Tr
                  key={key}
                  {...rowProps}
                  $selected={selected}
                  $clickable={Boolean(onRowClick)}
                  onClick={
                    onRowClick ? () => onRowClick(row.original) : undefined
                  }
                >
                  {selectable && (
                    <Td
                      $selected={selected}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <IndeterminateCheckbox
                        checked={selected}
                        onChange={() => selection.toggleRow(row.original)}
                      />
                    </Td>
                  )}
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                    return (
                      <Td key={cellKey} {...cellProps} $selected={selected}>
                        {cell.render("Cell")}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })
          )}
        </tbody>
      </Table>
    </TableWrap>
  );
}

export default DataTable;

const TableWrap = styled.div`
  width: 100%;
  font-family: "Noto Sans KR";
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e8e8e8;
`;

const Th = styled.th`
  height: 56px;
  padding: 8px;
  border: 1px solid #e8e8e8;
  background: #e1edff;
  color: var(--text-black, #111);
  font-family: "Noto Sans KR";
  font-size: 14px;
  font-weight: 400;
  text-align: center;
  white-space: nowrap;
  width: ${(props) => (props.$selectCell ? "44px" : "auto")};
`;

const Tr = styled.tr`
  border: 1px solid #e8e8e8;
  cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
  ${(props) =>
    props.$clickable && !props.$selected
      ? "&:hover td { background: #f7faff; }"
      : ""}
`;

const Td = styled.td`
  height: 56px;
  padding: 8px;
  border: 1px solid #e8e8e8;
  background: ${(props) => (props.$selected ? "#e6f7ff" : "white")};
  color: var(--text-black, #111);
  font-family: "Noto Sans KR";
  font-size: 14px;
  font-weight: 400;
  text-align: center;
`;

const EmptyTd = styled.td`
  height: 80px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  background: white;
  color: #8c8c8c;
  font-family: "Noto Sans KR";
  font-size: 14px;
  text-align: center;
`;
